// CharacterContextTrigger.jsx
// -----------------------------------------------------------------------------
// Inline ⓥ button that opens the Character Context Hover Panel.
//
// This is the *only* element host pages add inline next to a character name.
// It manages its own open/close state and mounts the popover lazily.
//
// Visual treatment: small circled-V glyph (U+24CB ⓥ — "voice character"), set
// in the accent grey of the host page so it sits quietly next to the
// character name. Opacity 0.55 at rest, 1.0 on hover/focus. The popover is
// the dramatic affordance — the trigger should not be.
//
// Carries `data-aai-cc-trigger="true"` so the popover's click-outside
// handler can recognize re-clicks on the trigger and ignore them (otherwise
// re-clicking the trigger would race the close animation).
//
// Spec reference: PHASE_4D_VA_OPPORTUNITY_CHARACTER_HOVER_PANEL_DESIGN.md §1.1
// (trigger affordance) and §2.1 (interaction policy).
// -----------------------------------------------------------------------------

import React, { useState, useCallback, useEffect, useId, useRef } from 'react';
import PropTypes from 'prop-types';
import { Button, Icon, Popup } from 'semantic-ui-react';
import CharacterContextPopover, { CLOSE_ANIM_MS } from './CharacterContextPopover';
// Styling for the actionButton variant. Overrides Semantic UI's baseline
// .ui.button rule via a (0,2,0)-specificity selector — see file header for
// the cascade-fight rationale. The inline ⓥ variant is styled by the
// `triggerButtonStyle` object below and does NOT depend on this stylesheet.
import './CharacterContextTrigger.css';


// Cross-instance singleton-popover coordination.
//
// Each trigger owns its own open-state — there is no shared store, no
// Context provider, no router-level slice. To enforce "only one hover
// panel docked at a time," every trigger dispatches a window-level
// CustomEvent when it opens and listens for the same event from peers.
// Receiving such an event with a different id closes this trigger's
// popover (via the normal setOpen(false) path, so the close animation
// still plays).
//
// Event name is namespaced; detail carries the opener's id only.
const AAI_CC_OPEN_EVENT = 'aai-cc:trigger-open';

// Module-level "who is currently open" slot. Survives across React
// component instances (modules are evaluated once per page). Used by the
// click handler to decide whether to open immediately (nobody open, or
// I'm the one open) or to first request a peer-close and *then* slide in
// after the close animation completes. Sequential handoff feels less
// jarring than two cards animating simultaneously.
let currentlyOpenTriggerId = null;



const triggerButtonStyle = {
  // Reset
  background: 'transparent',
  border: 0,
  padding: 0,
  margin: '0 0 0 6px',
  // Layout
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  verticalAlign: 'middle',
  // Glyph
  fontSize: '14px',
  lineHeight: 1,
  color: '#3a3833',
  opacity: 0.55,
  cursor: 'pointer',
  // Hit-target — slightly larger than the glyph for touch friendliness,
  // without changing the surrounding text baseline noticeably.
  minWidth: 18,
  minHeight: 18,
  borderRadius: '50%',
  // Smoother focus / hover
  transition: 'opacity 120ms ease-out, background-color 120ms ease-out',
};

const CharacterContextTrigger = ({
  bookUuid = undefined,
  characterId = undefined,
  characterName = undefined,
  bookTitle = undefined,
  title = undefined,
  variant = 'inline',
}) => {

  // The authorial-gold #d39e11 used to live in this local, dead-coded, as a
  // reminder of the color we wanted but couldn't pass through Semantic UI's
  // closed `color` enum. That hue now lives in CharacterContextTrigger.css
  // on `.ui.button.aai-cc-trigger--action-button`, so this local is gone.

  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(false);

  // Stable per-instance id so peer triggers can tell "open event came from
  // me" vs "from somebody else." useId() is React-18-provided, SSR-safe,
  // and survives re-renders without a useRef dance.
  const instanceId = useId();
  // Track the latest open state in a ref so the open-event listener (which
  // closes over the initial state) can read the current value without
  // re-binding on every state change.
  const openRef = useRef(open);
  useEffect(() => {
    openRef.current = open;
  }, [open]);

  // Holds the setTimeout id for a deferred-open (used when a peer's card
  // is still slide-out animating). Letting us cancel-and-replace if the
  // user re-clicks before the timer fires keeps the handoff coherent.
  const deferredOpenTimerRef = useRef(null);

  // Centralised "actually open this card" — broadcasts to peers, claims
  // the module-level slot, and flips local state. Pulled out so both the
  // immediate-open and deferred-open paths share one implementation.
  const performOpen = useCallback(() => {
    deferredOpenTimerRef.current = null;
    currentlyOpenTriggerId = instanceId;
    window.dispatchEvent(
      new CustomEvent(AAI_CC_OPEN_EVENT, { detail: { id: instanceId } })
    );
    setOpen(true);
  }, [instanceId]);

  const handleClick = useCallback((e) => {
    // Defensive: a trigger embedded inside a clickable row (e.g. spreadsheet
    // cell) should not propagate the click and trigger the row's onClick.
    e.stopPropagation();
    e.preventDefault();

    // If a previous click queued a deferred-open that hasn't fired yet,
    // cancel it — the user's latest click is the source of truth.
    if (deferredOpenTimerRef.current) {
      clearTimeout(deferredOpenTimerRef.current);
      deferredOpenTimerRef.current = null;
    }

    // Same-trigger toggle: snap closed immediately. No peer is involved.
    if (openRef.current) {
      if (currentlyOpenTriggerId === instanceId) {
        currentlyOpenTriggerId = null;
      }
      setOpen(false);
      return;
    }

    // No peer is currently open (or the only peer IS us, which the branch
    // above already handled) — open immediately, no handoff delay.
    if (!currentlyOpenTriggerId || currentlyOpenTriggerId === instanceId) {
      performOpen();
      return;
    }

    // A different trigger is currently open. Fire the close-peers event
    // now so the previous card starts its slide-out animation, then wait
    // for CLOSE_ANIM_MS before sliding our card in. This produces a
    // sequential close-then-open feel rather than two cards crossing.
    window.dispatchEvent(
      new CustomEvent(AAI_CC_OPEN_EVENT, { detail: { id: instanceId } })
    );
    deferredOpenTimerRef.current = setTimeout(() => {
      performOpen();
    }, CLOSE_ANIM_MS);
  }, [instanceId, performOpen]);

  const handleClose = useCallback(() => {
    if (currentlyOpenTriggerId === instanceId) {
      currentlyOpenTriggerId = null;
    }
    setOpen(false);
  }, [instanceId]);

  // Singleton-popover enforcement: when *another* trigger opens, close ours.
  // The opener has *already* claimed currentlyOpenTriggerId by the time
  // this event fires (performOpen does both), so we just route through
  // local setOpen and let the slide-out animation play.
  useEffect(() => {
    const onPeerOpen = (event) => {
      const peerId = event && event.detail && event.detail.id;
      if (!peerId || peerId === instanceId) return;
      // Also cancel any pending deferred-open we had queued; another
      // trigger beat us to the slot.
      if (deferredOpenTimerRef.current) {
        clearTimeout(deferredOpenTimerRef.current);
        deferredOpenTimerRef.current = null;
      }
      if (openRef.current) {
        setOpen(false);
      }
    };
    window.addEventListener(AAI_CC_OPEN_EVENT, onPeerOpen);
    return () => window.removeEventListener(AAI_CC_OPEN_EVENT, onPeerOpen);
  }, [instanceId]);

  // Cleanup any pending timer on unmount so we don't setOpen on a dead
  // component (e.g. trigger gets unmounted between click and timer fire).
  useEffect(
    () => () => {
      if (deferredOpenTimerRef.current) {
        clearTimeout(deferredOpenTimerRef.current);
        deferredOpenTimerRef.current = null;
      }
      if (currentlyOpenTriggerId === instanceId) {
        currentlyOpenTriggerId = null;
      }
    },
    [instanceId]
  );



  // Inline-style hover handling so we don't have to ship a stylesheet for
  // the trigger itself. The popover gets its own dedicated stylesheet
  // because it has more states to animate; the trigger is just a button.
  const style = {
    ...triggerButtonStyle,
    opacity: open || hovered ? 1 : 0.55,
    backgroundColor: hovered ? 'rgba(58, 56, 51, 0.08)' : 'transparent',
  };

  // U+24CB CIRCLED LATIN CAPITAL LETTER V — the design-doc nominal glyph.
  const glyph = '\u24CB';

  const accessibleTitle =
    title ||
    (characterName
      ? `View voice context for ${characterName}`
      : 'View voice context');

  // Render the click target. Two flavours:
  //
  //   variant="inline" (default) — the original ⓥ glyph button. Used next
  //     to a character name in OpportunityCard, OpportunityDetails, and
  //     other prose-context surfaces. Carries the inline-style hover state
  //     above.
  //
  //   variant="actionButton" — Semantic UI <Button size="tiny" compact icon>
  //     wrapped in <Popup> for tooltip, painted authorial-gold (#d39e11)
  //     by CharacterContextTrigger.css. Used in cells that already hold a
  //     row of Semantic-UI icon buttons (e.g. the Actions cell in
  //     OpportunitySpreadsheet) so the trigger reads as a peer of the
  //     View-Details and Apply-Now buttons rather than a stray inline
  //     glyph. Operator will likely swap the placeholder icon later — for
  //     now it shows "address card outline" as a stand-in for "voice
  //     character context."
  //
  // Both variants share the same handler stack and the same popover mount
  // below; only the click-target chrome differs.
  const triggerNode = variant === 'actionButton' ? (
    <Popup
      content={accessibleTitle}
      position="top center"
      trigger={
        <Button
          data-aai-cc-trigger="true"
          className="aai-cc-trigger aai-cc-trigger--action-button"
          // No `color` prop — Semantic UI's `color` is a closed enum of
          // named hues and won't accept #d39e11. The button's background
          // is painted by CharacterContextTrigger.css instead, which
          // overrides Semantic's baseline `.ui.button` rule.
          size="tiny"
          compact
          icon
          onClick={handleClick}
          aria-label={accessibleTitle}
          aria-expanded={open}
          aria-haspopup="dialog"
        >
          {/* Placeholder icon — operator will swap after design time.
              "address card outline" reads as "character profile / dossier"
              which is in the ballpark for "voice character context." */}
          <Icon name="address card outline" />
        </Button>
      }
    />
  ) : (
    <button
      type="button"
      data-aai-cc-trigger="true"
      className="aai-cc-trigger"
      style={style}
      onClick={handleClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
      title={accessibleTitle}
      aria-label={accessibleTitle}
      aria-expanded={open}
      aria-haspopup="dialog"
    >
      {glyph}
    </button>
  );

  return (
    <>
      {triggerNode}
      <CharacterContextPopover
        open={open}
        onClose={handleClose}
        bookUuid={bookUuid}
        characterId={characterId}
        characterName={characterName}
        bookTitle={bookTitle}
      />

    </>
  );
};


CharacterContextTrigger.propTypes = {
  /** Book UUID. Required alongside characterId for the live-fetch branch of
   *  the resolver hook (Chunk 6). When absent, the hook falls back to the
   *  mock fixture branch, which is intended only for the demo route. */
  bookUuid: PropTypes.string,
  /** Stable character identifier (UUID). Required alongside bookUuid for
   *  live-data resolution. */
  characterId: PropTypes.string,
  /** Display name. Used by the mock resolver for deterministic cycling AND
   *  rendered as the panel's loading-state header text. */
  characterName: PropTypes.string,
  /** Book title displayed under the role/lines/chapters header line in the
   *  panel. Pass the owning opportunity's book name here. */
  bookTitle: PropTypes.string,
  /** Override for the accessible title / aria-label. Optional. */
  title: PropTypes.string,
  /** Visual flavour of the click target.
   *
   *   - "inline" (default) — small ⓥ glyph button intended to sit next to
   *     a character name in prose context (OpportunityCard,
   *     OpportunityDetails).
   *   - "actionButton" — Semantic UI <Button compact icon> wrapped in
   *     <Popup>, intended for cells already populated by Semantic UI icon
   *     buttons (e.g. the Actions cell in OpportunitySpreadsheet) so the
   *     trigger reads as a peer rather than a stray inline glyph.
   */
  variant: PropTypes.oneOf(['inline', 'actionButton']),
};

export default CharacterContextTrigger;
